import { ajax } from 'discourse/lib/ajax';
import DiscourseRoute from 'discourse/routes/discourse';

const TIER_LABEL = {
  basic: 'Verified',
  benchmark: 'Benchmark Verified',
  premium: 'Premium Verified',
};

// Derive a logo: explicit logo_url, else the website's favicon (high-res).
export function logoFor(p) {
  if (p.logo_url) {
    return p.logo_url;
  }
  if (p.website_url) {
    try {
      const host = new URL(p.website_url).hostname;
      return `https://www.google.com/s2/favicons?domain=${host}&sz=64`;
    } catch (e) {
      return null;
    }
  }
  return null;
}

// Shared shape transform so both the initial route load and any later
// client-driven fetch (e.g. from the search/filter UI) render identically.
export function mapProvider(raw) {
  const p = raw.provider || raw;
  const tier = p.verification_tier;
  const verified = tier && tier !== 'none';
  return {
    ...p,
    tierClass: verified ? `pd-badge--verified-${tier}` : null,
    tierLabel: verified ? TIER_LABEL[tier] || 'Verified' : null,
    cardClass: p.featured
      ? 'pd-card--featured'
      : tier === 'premium'
        ? 'pd-card--premium'
        : '',
    costDisplay: (p.price_range_string || p.real_cost_per_gb) ? `$${p.price_range_string || p.real_cost_per_gb}` : '—',
    ratingDisplay: p.overall_rating ? Number(p.overall_rating).toFixed(1) : '—',
    benchmarkDisplay: p.benchmark_score ? Number(p.benchmark_score).toFixed(1) : '—',
    logoInitial: (p.name || '?').charAt(0).toUpperCase(),
    logo: logoFor(p),
    providerUrl: `/providers/${p.slug}`,
    affiliateUrl: p.affiliate_url || (p.profile && p.profile.affiliate_url),
  };
}

// Default query params, mirrored by ProxyviewsDirectoryBrowser's initial
// tracked state, so the server-rendered first page and the client-side
// filter UI start out in sync.
export const DEFAULT_DIRECTORY_PARAMS = { sort: 'rating', page: 1 };

export async function fetchProviders(params = {}) {
  const query = { ...DEFAULT_DIRECTORY_PARAMS, ...params };
  const data = { sort: query.sort, page: query.page };
  // Multi-select type filter: send selected types as comma-separated
  // proxy_type param (e.g. "residential,datacenter"). The backend matches
  // providers that offer ANY of the selected types.
  if (query.types && query.types.length) {
    data.proxy_type = query.types.join(',');
  }
  if (query.verified) {
    data.verified = 'true';
  }
  if (query.q && query.q.trim().length) {
    data.q = query.q.trim();
  }
  // Send max_price to the backend instead of filtering client-side —
  // previously the slider only filtered the current page, missing
  // matching providers on other pages and showing wrong result counts.
  if (query.maxPrice && query.maxPrice < 30) {
    data.max_price = query.maxPrice;
  }

  const result = await ajax('/providers.json', { data });
  const meta = result.meta || {};

  // Pre-format the hero review/rating values so the template can render them
  // directly (RouteTemplate has no backing class for helpers).
  const reviewCount = Number(meta.review_count || 0);
  meta.review_count_label =
    reviewCount >= 1000
      ? (reviewCount / 1000).toFixed(1).replace(/\.0$/, "") + "k+"
      : reviewCount.toLocaleString();
  const avgRating = Number(meta.avg_rating || 0);
  meta.avg_rating_label = avgRating > 0 ? avgRating.toFixed(1) + "★" : "—";

  return {
    providers: (result.providers || []).map(mapProvider),
    meta,
  };
}

export default class ProxyviewsDirectoryRoute extends DiscourseRoute {
  async model() {
    return fetchProviders();
  }

  titleToken() {
    return 'Proxy Provider Directory';
  }
}
